import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { agentManagementService } from '../../services/agentManagementService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import AgentCard from './components/AgentCard';
import SearchBar from './components/SearchBar';
import FilterPopover from './components/FilterPopover';
import CreateAgentButton from './components/CreateAgentButton';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import EmptyState from './components/EmptyState';
import StatsOverview from './components/StatsOverview';

const AIAgentsDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    tags: [],
    model: 'all',
    dateRange: 'all',
    activity: 'all'
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, agentId: null, agentName: '' });
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load agents from Supabase
  const loadAgents = async () => {
    if (!user) {
      setAgents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await agentManagementService?.getAgentsWithStats();
      setAgents(data || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setError('Failed to load agents. Please try again.');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // Load agents on component mount and user change
  useEffect(() => {
    if (!authLoading) {
      loadAgents();
    }
  }, [user, authLoading]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const channel = agentManagementService?.subscribeToChanges((payload) => {
      console.log('Agent change detected:', payload);
      
      switch (payload?.eventType) {
        case 'INSERT':
          setAgents(prev => [payload?.new, ...prev]);
          break;
        case 'UPDATE':
          setAgents(prev => prev?.map(agent => 
            agent?.id === payload?.new?.id ? { ...agent, ...payload?.new } : agent
          ));
          break;
        case 'DELETE':
          setAgents(prev => prev?.filter(agent => agent?.id !== payload?.old?.id));
          break;
        default:
          // Reload all agents for other cases
          loadAgents();
          break;
      }
    });

    return () => {
      agentManagementService?.unsubscribeFromChanges(channel);
    };
  }, [user]);

  // Filter and search logic
  const filteredAgents = useMemo(() => {
    let filtered = [...agents];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(agent => 
        agent?.name?.toLowerCase()?.includes(searchLower) ||
        agent?.id?.toLowerCase()?.includes(searchLower) ||
        agent?.tags?.some(tag => tag?.toLowerCase()?.includes(searchLower)) ||
        agent?.display_name?.toLowerCase()?.includes(searchLower)
      );
    }

    // Tags filter
    if (filters?.tags?.length > 0) {
      filtered = filtered?.filter(agent =>
        filters?.tags?.some(tag => agent?.tags?.includes(tag))
      );
    }

    // Model filter
    if (filters?.model !== 'all') {
      filtered = filtered?.filter(agent => agent?.model === filters?.model);
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters?.dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          break;
        case 'quarter':
          filterDate?.setMonth(now?.getMonth() - 3);
          break;
        case 'year':
          filterDate?.setFullYear(now?.getFullYear() - 1);
          break;
        default:
          break;
      }
      
      if (filters?.dateRange !== 'all') {
        filtered = filtered?.filter(agent => new Date(agent?.created_at) >= filterDate);
      }
    }

    // Activity filter
    if (filters?.activity !== 'all') {
      filtered = filtered?.filter(agent => 
        filters?.activity === 'active' ? agent?.isActive : !agent?.isActive
      );
    }

    // Sort: pinned first, then by creation date
    return filtered?.sort((a, b) => {
      if (a?.isPinned && !b?.isPinned) return -1;
      if (!a?.isPinned && b?.isPinned) return 1;
      return new Date(b?.created_at) - new Date(a?.created_at);
    });
  }, [agents, searchTerm, filters]);

  // Agent actions
  const handleViewAgent = (agentId) => {
    // Redirect to agent configuration instead of the removed chat testing interface
    navigate(`/agent-configuration?agent=${agentId}`);
  };

  const handleEditAgent = (agentId) => {
    navigate(`/agent-configuration?agent=${agentId}`);
  };

  const handleShareAgent = async (agentId) => {
    try {
      // Use the correct domain for share links
      const shareDomain = 'https://chatbots.vierdimensional.ai';
      const shareUrl = `${shareDomain}/chat/${agentId}`;
      await navigator.clipboard?.writeText(shareUrl);
      // TODO: Add toast notification
      console.log(`Share link copied for agent ${agentId}:`, shareUrl);
    } catch (error) {
      console.error('Failed to copy share link:', error);
    }
  };

  const handleCloneAgent = async (agentId) => {
    try {
      const clonedAgent = await agentManagementService?.cloneAgent(agentId);
      console.log('Agent cloned successfully:', clonedAgent?.id);
      // Agent will be added to UI via real-time subscription
    } catch (error) {
      console.error('Failed to clone agent:', error);
      setError('Failed to clone agent. Please try again.');
    }
  };

  const handleMigrateAgent = (agentId) => {
    // TODO: Implement migration functionality
    console.log(`Migrating agent ${agentId}`);
  };

  const handleCopyEmbedCode = async (agentId) => {
    try {
      // Use the correct domain for embed generation
      const embedDomain = 'https://chatbots.vierdimensional.ai';
      const embedCode = `<iframe src="${embedDomain}/embed/${agentId}" width="400" height="600" frameborder="0"></iframe>`;
      await navigator.clipboard?.writeText(embedCode);
      console.log(`Embed code copied for agent ${agentId}:`, embedCode);
    } catch (error) {
      console.error('Failed to copy embed code:', error);
    }
  };

  const handleCopySupportInfo = async (agentId) => {
    try {
      const agent = agents?.find(a => a?.id === agentId);
      const supportDomain = 'https://chatbots.vierdimensional.ai';
      const supportInfo = `Agent: ${agent?.name || 'Unknown'}\nID: ${agent?.id}\nModel: ${agent?.model || 'Unknown'}\nEmbed URL: ${supportDomain}/embed/${agentId}\nSupport: support@vibecoding.com`;
      await navigator.clipboard?.writeText(supportInfo);
      console.log(`Support info copied for agent ${agentId}`);
    } catch (error) {
      console.error('Failed to copy support info:', error);
    }
  };

  const handlePinAgent = (agentId) => {
    // TODO: Implement pin functionality with Supabase
    console.log(`Toggling pin for agent ${agentId}`);
  };

  const handleAddToFolder = (agentId) => {
    // TODO: Implement folder functionality
    console.log(`Adding agent ${agentId} to folder`);
  };

  const handleDeleteAgent = (agentId) => {
    const agent = agents?.find(a => a?.id === agentId);
    setDeleteModal({
      isOpen: true,
      agentId,
      agentName: agent?.name || agent?.display_name || 'Unknown Agent'
    });
  };

  const confirmDeleteAgent = async () => {
    if (!deleteModal?.agentId) return;

    try {
      setDeleteLoading(true);
      setError(null);
      
      console.log(`Deleting agent ${deleteModal?.agentId}`);
      await agentManagementService?.deleteAgent(deleteModal?.agentId);
      
      // Close modal
      setDeleteModal({ isOpen: false, agentId: null, agentName: '' });
      
      // Agent will be removed from UI via real-time subscription
      console.log('Agent deleted successfully');
    } catch (error) {
      console.error('Failed to delete agent:', error);
      setError('Failed to delete agent. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const hasActiveFilters = filters?.tags?.length > 0 || 
                          filters?.model !== 'all' || 
                          filters?.dateRange !== 'all' || 
                          filters?.activity !== 'all';

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show sign-in prompt if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md mx-auto px-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Sign In Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to access your AI agents dashboard and manage your chatbots.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pb-16 lg:pb-0`}>
          <div className="p-6">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-foreground mb-2">AI Agents Dashboard</h1>
              <p className="text-muted-foreground">
                Verwalten Sie Ihre AI-Chatbots und überwachen Sie deren Leistung
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
                <p className="text-sm">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 text-xs underline mt-1"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Stats Overview */}
            <StatsOverview agents={agents} />

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <SearchBar 
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                />
                <FilterPopover 
                  filters={filters}
                  onFiltersChange={setFilters}
                  resultCount={filteredAgents?.length}
                />
              </div>
              <CreateAgentButton />
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading agents...</p>
                </div>
              </div>
            ) : (
              /* Agents Grid */
              (filteredAgents?.length > 0 ? (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredAgents?.map((agent) => (
                  <AgentCard
                    key={agent?.id}
                    agent={agent}
                    onView={handleViewAgent}
                    onEdit={handleEditAgent}
                    onShare={handleShareAgent}
                    onClone={handleCloneAgent}
                    onMigrate={handleMigrateAgent}
                    onCopyEmbed={handleCopyEmbedCode}
                    onCopySupport={handleCopySupportInfo}
                    onPin={handlePinAgent}
                    onAddToFolder={handleAddToFolder}
                    onDelete={handleDeleteAgent}
                  />
                ))}
              </div>) : (<EmptyState 
                hasSearchTerm={!!searchTerm}
                searchTerm={searchTerm}
                hasFilters={hasActiveFilters}
              />))
            )}
          </div>
        </main>
      </div>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal?.isOpen}
        onClose={() => !deleteLoading && setDeleteModal({ isOpen: false, agentId: null, agentName: '' })}
        onConfirm={confirmDeleteAgent}
        agentName={deleteModal?.agentName}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default AIAgentsDashboard;